import {
  CardMeta,
  CardMetaGrid,
  ManagementCard,
  ManagementCardList,
  Pill,
} from 'components/ManagementCard';
import { commonStrings, summaryManagementStrings } from 'constants/strings';
import moment from 'moment-timezone';
import type {
  TLegacySummaryRow,
  TSummaryMode,
  TSummaryRow,
} from 'types/summary';
import {
  formatSummaryClickRate,
  formatSummaryCount,
  resolveSummaryListMessage,
  resolveUnavailableRecipientBadge,
  resolveUnknownRecipientBadge,
  summaryStatusTone,
} from './summaryDisplay';

type SummaryDisplayRow = TSummaryRow | TLegacySummaryRow;

type SummaryCardListProps = {
  mode: TSummaryMode;
  rows: SummaryDisplayRow[];
  loading: boolean;
  /** 取得失敗。空一覧を「該当0件」と誤読させないため空メッセージを出さない */
  hasError?: boolean;
  onSelect: (row: SummaryDisplayRow) => void;
  className?: string;
};

function formatDate(iso: string): string {
  return moment(iso).tz('Asia/Tokyo').format('YYYY年M月D日 HH:mm');
}

function formatNumber(value: number | null): string {
  return value == null
    ? summaryManagementStrings.unavailableMetric
    : value.toLocaleString('ja-JP');
}

function isLegacyRow(row: SummaryDisplayRow): row is TLegacySummaryRow {
  return 'sentAt' in row;
}

export function SummaryCardList({
  mode,
  rows,
  loading,
  hasError = false,
  onSelect,
  className,
}: SummaryCardListProps) {
  return (
    <ManagementCardList
      loading={loading}
      isEmpty={rows.length === 0}
      loadingMessage={commonStrings.loading}
      emptyMessage={
        resolveSummaryListMessage({
          mode,
          loading: false,
          hasError,
          rowCount: rows.length,
        }) ?? ''
      }
      className={className}
    >
      {rows.map((row) => (
        <li key={row.id}>
          {isLegacyRow(row) ? (
            <LegacySummaryCard row={row} onSelect={onSelect} />
          ) : (
            <ClickSummaryCard row={row} onSelect={onSelect} />
          )}
        </li>
      ))}
    </ManagementCardList>
  );
}

function ClickSummaryCard({
  row,
  onSelect,
}: {
  row: TSummaryRow;
  onSelect: SummaryCardListProps['onSelect'];
}) {
  const unavailableBadge = resolveUnavailableRecipientBadge(row);
  const unknownBadge = resolveUnknownRecipientBadge(row);
  return (
    <ManagementCard
      onClick={() => onSelect(row)}
      ariaLabel={`${formatDate(row.startedAt)} ${summaryManagementStrings.table.detail}`}
      title={formatDate(row.startedAt)}
      badges={
        <>
          <Pill>{summaryManagementStrings.sourceTypes[row.sourceType]}</Pill>
          <Pill tone={summaryStatusTone[row.status]}>
            {summaryManagementStrings.deliveryStatus[row.status]}
          </Pill>
          {row.coverage !== 'FULL' && (
            <Pill tone="warning">
              {summaryManagementStrings.coverageWarnings[row.coverage]}
            </Pill>
          )}
          {unavailableBadge && <Pill tone="warning">{unavailableBadge}</Pill>}
          {unknownBadge && <Pill tone="warning">{unknownBadge}</Pill>}
        </>
      }
    >
      {row.sourceLabel && (
        <p className="truncate text-xs text-muted">{row.sourceLabel}</p>
      )}
      <CardMetaGrid cols={2}>
        <CardMeta
          label={summaryManagementStrings.table.sentRecipientCount}
          value={formatNumber(row.sentRecipientCount)}
        />
        <CardMeta
          label={summaryManagementStrings.table.totalClicks}
          value={formatNumber(row.totalClicks)}
        />
        <CardMeta
          label={summaryManagementStrings.table.uniqueClickRecipientCount}
          value={formatSummaryCount(row.uniqueClickRecipientCount)}
        />
        <CardMeta
          label={summaryManagementStrings.table.clickRate}
          value={formatSummaryClickRate(row.clickRate)}
        />
      </CardMetaGrid>
    </ManagementCard>
  );
}

function LegacySummaryCard({
  row,
  onSelect,
}: {
  row: TLegacySummaryRow;
  onSelect: SummaryCardListProps['onSelect'];
}) {
  const openedRate =
    row.deliveryCount > 0
      ? `${Math.round(
          (row.legacyOpenedCount / row.deliveryCount) * 100,
        )}%`
      : summaryManagementStrings.unavailableMetric;

  return (
    <ManagementCard
      onClick={() => onSelect(row)}
      ariaLabel={`${formatDate(row.sentAt)} ${summaryManagementStrings.table.contentConfirm}`}
      title={formatDate(row.sentAt)}
    >
      <CardMetaGrid cols={3}>
        <CardMeta
          label={summaryManagementStrings.table.legacyDeliveryCount}
          value={formatNumber(row.deliveryCount)}
        />
        <CardMeta
          label={summaryManagementStrings.table.legacyOpenedCount}
          value={formatNumber(row.legacyOpenedCount)}
        />
        <CardMeta
          label={summaryManagementStrings.table.legacyOpenedRate}
          value={openedRate}
        />
      </CardMetaGrid>
    </ManagementCard>
  );
}
